export const STORY_MEDIA_TYPES = Object.freeze(['image', 'video']);
export const STORY_AUDIENCE_PUBLIC_PROFILE = 'public_profile';
export const STORY_DURATION_MIN_HOURS = 1;
export const STORY_DURATION_MAX_HOURS = 168;
export const STORY_DEFAULT_DURATION_HOURS = 24;
export const STORY_CAPTION_MAX_LENGTH = 220;

export const STORY_VALIDATION_KEYS = Object.freeze({
  mediaUrlRequired: 'mediaUrlRequired',
  mediaUrlInvalid: 'mediaUrlInvalid',
  mediaTypeInvalid: 'mediaTypeInvalid',
  durationInvalid: 'durationInvalid',
  captionTooLong: 'captionTooLong',
  audienceUnsupported: 'audienceUnsupported',
});

export const STORY_UNSUPPORTED_ENDPOINT_STATUSES = Object.freeze([404, 405]);

const toNumber = value => Number(value);

const isValidUrl = value => {
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const present = value => value !== null && value !== undefined && value !== '';

export const defaultStoryForm = () => ({
  media_url: '',
  media_type: 'image',
  caption: '',
  imovel_id: '',
  duration_hours: STORY_DEFAULT_DURATION_HOURS,
  audience: STORY_AUDIENCE_PUBLIC_PROFILE,
});

export const durationFromStory = story => {
  const createdAt = Date.parse(story.createdAt || story.created_at || '');
  const expiresAt = Date.parse(story.expiresAt || story.expires_at || '');
  if (!Number.isFinite(createdAt) || !Number.isFinite(expiresAt)) {
    return STORY_DEFAULT_DURATION_HOURS;
  }

  const hours = Math.round((expiresAt - createdAt) / 36e5);
  return Math.min(
    STORY_DURATION_MAX_HOURS,
    Math.max(STORY_DURATION_MIN_HOURS, hours || STORY_DEFAULT_DURATION_HOURS)
  );
};

export const formFromStory = story => ({
  media_url: story.mediaUrl || story.media_url || '',
  media_type: story.mediaType || story.media_type || 'image',
  caption: story.caption || '',
  imovel_id: story.imovelId || story.imovel_id || '',
  duration_hours: durationFromStory(story),
  audience: STORY_AUDIENCE_PUBLIC_PROFILE,
});

export const validateStoryForm = form => {
  const errors = {};
  const mediaUrl = String(form.media_url || '').trim();
  const duration = toNumber(form.duration_hours);
  const caption = String(form.caption || '');

  if (!mediaUrl) {
    errors.media_url = STORY_VALIDATION_KEYS.mediaUrlRequired;
  } else if (!isValidUrl(mediaUrl)) {
    errors.media_url = STORY_VALIDATION_KEYS.mediaUrlInvalid;
  }

  if (!STORY_MEDIA_TYPES.includes(form.media_type)) {
    errors.media_type = STORY_VALIDATION_KEYS.mediaTypeInvalid;
  }

  if (
    !Number.isInteger(duration) ||
    duration < STORY_DURATION_MIN_HOURS ||
    duration > STORY_DURATION_MAX_HOURS
  ) {
    errors.duration_hours = STORY_VALIDATION_KEYS.durationInvalid;
  }

  if (caption.length > STORY_CAPTION_MAX_LENGTH) {
    errors.caption = STORY_VALIDATION_KEYS.captionTooLong;
  }

  if (form.audience !== STORY_AUDIENCE_PUBLIC_PROFILE) {
    errors.audience = STORY_VALIDATION_KEYS.audienceUnsupported;
  }

  return errors;
};

export const hasValidationErrors = errors =>
  Object.keys(errors || {}).length > 0;

export const buildStoryPayload = form => {
  const payload = {
    media_url: String(form.media_url || '').trim(),
    media_type: form.media_type || 'image',
    caption: String(form.caption || '').trim(),
    duration_hours: toNumber(form.duration_hours),
  };

  if (present(form.imovel_id)) {
    payload.imovel_id = String(form.imovel_id).trim();
  }

  return payload;
};

export const normalizeStory = story => {
  const expiresAt = story.expiresAt || story.expires_at || '';
  const expired =
    expiresAt && Number.isFinite(Date.parse(expiresAt))
      ? Date.parse(expiresAt) <= Date.now()
      : false;

  return {
    id: String(story.id || ''),
    username: story.username || '',
    mediaUrl: story.mediaUrl || story.media_url || '',
    mediaType: STORY_MEDIA_TYPES.includes(story.mediaType || story.media_type)
      ? story.mediaType || story.media_type
      : 'image',
    caption: story.caption || '',
    imovelId: story.imovelId || story.imovel_id || '',
    createdAt: story.createdAt || story.created_at || '',
    expiresAt,
    viewsCount: Number(story.viewsCount || story.views_count || 0),
    isExpired: expired,
    status: expired ? 'expired' : 'active',
  };
};

export const normalizeStoriesResponse = data => {
  const records = Array.isArray(data?.stories) ? data.stories : [];
  return records.map(normalizeStory);
};

export const summarizeStories = stories => {
  const normalized = stories.map(normalizeStory);
  return {
    total: normalized.length,
    active: normalized.filter(story => !story.isExpired).length,
    expired: normalized.filter(story => story.isExpired).length,
    views: normalized.reduce((sum, story) => sum + story.viewsCount, 0),
  };
};

export const isUnsupportedEndpointError = error =>
  STORY_UNSUPPORTED_ENDPOINT_STATUSES.includes(error?.response?.status);

export const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  const errors = Array.isArray(data.errors) ? data.errors.filter(Boolean) : [];
  return errors[0] || data.error || data.message || fallback;
};
